import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router";
import {
  Alert,
  App as AntdApp,
  Button,
  Card,
  Form,
  Input,
  Modal,
  Popconfirm,
  Radio,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
} from "antd";
import {
  DeleteOutlined,
  EditOutlined,
  HistoryOutlined,
  MergeCellsOutlined,
  PlusOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";

import { apiClient } from "../../../services/api/apiClient";
import { PageHeader } from "../../../components/common/PageHeader";
import { TreeIcon } from "../../../components/common/TreeIcon";
import { usePageTitle } from "../../../hooks";

const { Text } = Typography;

interface Species {
  id: string;
  name: string;
  name_en: string;
  name_it: string;
  name_pt: string;
  scientific_name: string | null;
  description: string | null;
  is_active: boolean;
  variety_count: number;
  assignment_count: number;
}

interface Variety {
  id: string;
  name: string;
  name_en: string;
  name_it: string;
  name_pt: string;
  species: string;
  species_name: string;
  description: string | null;
  is_active: boolean;
  assignment_count: number;
}

interface SpeciesFormValues {
  name: string;
  name_en?: string;
  name_it?: string;
  name_pt?: string;
  scientific_name?: string;
}

interface VarietyFormValues {
  name: string;
  name_en?: string;
  name_it?: string;
  name_pt?: string;
  species: string;
}

type EntityKind = "species" | "variety";

interface MergeContext {
  kind: EntityKind;
  ids: string[];
  /** Only set for variety merges — drives the same-species guard. */
  speciesId?: string;
}

/**
 * Admin page for `CropSpecies` + `CropVariety` — the master data
 * referenced by the adjacent-crops module. Species are shown in the
 * main table; each row expands to reveal its varieties (children).
 *
 * Both levels support CRUD + bulk merge with the same delete-guard
 * pattern as the other admin master-data pages.
 *
 * Constraint: varieties can only be merged with other varieties from
 * the **same parent species** — the backend enforces this too. The
 * merge button on a varieties row is hidden when the selection spans
 * multiple species.
 */
export const CropsList = () => {
  const { t } = useTranslation();
  const { message: notify } = AntdApp.useApp();

  // Data
  const [species, setSpecies] = useState<Species[]>([]);
  const [varietiesBySpecies, setVarietiesBySpecies] = useState<
    Record<string, Variety[]>
  >({});
  const [loading, setLoading] = useState(true);

  // Selection (species level)
  const [selectedSpeciesIds, setSelectedSpeciesIds] = useState<string[]>([]);

  // Selection (variety level) — tracks per-species-row keys plus a flat
  // list for the merge action.
  const [selectedVarietyIds, setSelectedVarietyIds] = useState<string[]>([]);

  // Editor (used for both species + variety, switched via `editorKind`).
  const [editorOpen, setEditorOpen] = useState(false);
  const [editorKind, setEditorKind] = useState<EntityKind>("species");
  const [editingSpecies, setEditingSpecies] = useState<Species | null>(null);
  const [editingVariety, setEditingVariety] = useState<Variety | null>(null);
  // For "new variety", we need to remember which species we're creating under.
  const [varietyParentSpeciesId, setVarietyParentSpeciesId] = useState<
    string | null
  >(null);
  const [speciesForm] = Form.useForm<SpeciesFormValues>();
  const [varietyForm] = Form.useForm<VarietyFormValues>();
  const [saving, setSaving] = useState(false);

  // Merge modal
  const [mergeContext, setMergeContext] = useState<MergeContext | null>(null);
  const [mergeTargetId, setMergeTargetId] = useState<string | null>(null);
  const [merging, setMerging] = useState(false);

  usePageTitle(t("crops.title", "Especies y variedades de cultivo"));

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [spRes, vRes] = await Promise.all([
        apiClient.get<Species[]>("/admin/crop-species/"),
        apiClient.get<Variety[]>("/admin/crop-varieties/"),
      ]);
      const spList = Array.isArray(spRes.data)
        ? spRes.data
        : ((spRes.data as any).results ?? []);
      const vList = Array.isArray(vRes.data)
        ? vRes.data
        : ((vRes.data as any).results ?? []);
      setSpecies(spList);
      const grouped: Record<string, Variety[]> = {};
      for (const v of vList) {
        (grouped[v.species] ||= []).push(v);
      }
      setVarietiesBySpecies(grouped);
    } catch {
      notify.error(t("crops.load_error", "Error al cargar los cultivos"));
      setSpecies([]);
      setVarietiesBySpecies({});
    } finally {
      setLoading(false);
    }
  }, [notify, t]);

  useEffect(() => {
    void load();
  }, [load]);

  // ----- species CRUD ------------------------------------------------

  const openCreateSpecies = () => {
    setEditorKind("species");
    setEditingSpecies(null);
    speciesForm.resetFields();
    setEditorOpen(true);
  };

  const openEditSpecies = (row: Species) => {
    setEditorKind("species");
    setEditingSpecies(row);
    speciesForm.setFieldsValue({
      name: row.name,
      name_en: row.name_en ?? "",
      name_it: row.name_it ?? "",
      name_pt: row.name_pt ?? "",
      scientific_name: row.scientific_name ?? "",
    });
    setEditorOpen(true);
  };

  const deleteSpecies = async (row: Species) => {
    try {
      await apiClient.delete(`/admin/crop-species/${row.id}/`);
      notify.success(t("crops.species_delete_success", "Especie eliminada"));
      setSelectedSpeciesIds((keys) => keys.filter((k) => k !== row.id));
      await load();
    } catch (e: any) {
      const msg =
        e?.response?.data?.detail ??
        t("crops.species_delete_error", "No se pudo eliminar la especie");
      notify.error(msg);
    }
  };

  // ----- variety CRUD ------------------------------------------------

  const openCreateVariety = (parentSpeciesId: string) => {
    setEditorKind("variety");
    setEditingVariety(null);
    setVarietyParentSpeciesId(parentSpeciesId);
    varietyForm.resetFields();
    varietyForm.setFieldsValue({ species: parentSpeciesId });
    setEditorOpen(true);
  };

  const openEditVariety = (row: Variety) => {
    setEditorKind("variety");
    setEditingVariety(row);
    setVarietyParentSpeciesId(row.species);
    varietyForm.setFieldsValue({
      name: row.name,
      name_en: row.name_en ?? "",
      name_it: row.name_it ?? "",
      name_pt: row.name_pt ?? "",
      species: row.species,
    });
    setEditorOpen(true);
  };

  const deleteVariety = async (row: Variety) => {
    try {
      await apiClient.delete(`/admin/crop-varieties/${row.id}/`);
      notify.success(t("crops.variety_delete_success", "Variedad eliminada"));
      setSelectedVarietyIds((keys) => keys.filter((k) => k !== row.id));
      await load();
    } catch (e: any) {
      const msg =
        e?.response?.data?.detail ??
        t("crops.variety_delete_error", "No se pudo eliminar la variedad");
      notify.error(msg);
    }
  };

  // ----- editor submit ----------------------------------------------

  const submitEditor = async () => {
    setSaving(true);
    try {
      if (editorKind === "species") {
        const values = await speciesForm.validateFields();
        if (editingSpecies) {
          await apiClient.patch(
            `/admin/crop-species/${editingSpecies.id}/`,
            values,
          );
          notify.success(
            t("crops.species_update_success", "Especie actualizada"),
          );
        } else {
          await apiClient.post("/admin/crop-species/", values);
          notify.success(t("crops.species_create_success", "Especie creada"));
        }
      } else {
        const values = await varietyForm.validateFields();
        if (editingVariety) {
          await apiClient.patch(
            `/admin/crop-varieties/${editingVariety.id}/`,
            values,
          );
          notify.success(
            t("crops.variety_update_success", "Variedad actualizada"),
          );
        } else {
          await apiClient.post("/admin/crop-varieties/", values);
          notify.success(t("crops.variety_create_success", "Variedad creada"));
        }
      }
      setEditorOpen(false);
      await load();
    } catch (e: any) {
      const msg =
        e?.response?.data?.detail ??
        e?.response?.data?.name?.[0] ??
        t("crops.save_error", "No se pudo guardar");
      notify.error(msg);
    } finally {
      setSaving(false);
    }
  };

  // ----- merge -------------------------------------------------------

  const speciesMergeRows = useMemo(
    () => species.filter((s) => selectedSpeciesIds.includes(s.id)),
    [species, selectedSpeciesIds],
  );

  const flatVarieties = useMemo(() => {
    const acc: Variety[] = [];
    Object.values(varietiesBySpecies).forEach((arr) => acc.push(...arr));
    return acc;
  }, [varietiesBySpecies]);

  const varietyMergeRows = useMemo(
    () => flatVarieties.filter((v) => selectedVarietyIds.includes(v.id)),
    [flatVarieties, selectedVarietyIds],
  );

  /** Distinct species ids represented in the variety selection. */
  const varietySelectionSpeciesIds = useMemo(
    () => Array.from(new Set(varietyMergeRows.map((v) => v.species))),
    [varietyMergeRows],
  );

  const openSpeciesMerge = () => {
    if (selectedSpeciesIds.length < 2) {
      notify.warning(
        t(
          "crops.species_merge_need_two",
          "Selecciona al menos dos especies para fusionar.",
        ),
      );
      return;
    }
    const heaviest = [...speciesMergeRows].sort(
      (a, b) => b.assignment_count - a.assignment_count,
    )[0];
    setMergeContext({ kind: "species", ids: selectedSpeciesIds });
    setMergeTargetId(heaviest?.id ?? selectedSpeciesIds[0]);
  };

  const openVarietyMerge = () => {
    if (selectedVarietyIds.length < 2) {
      notify.warning(
        t(
          "crops.variety_merge_need_two",
          "Selecciona al menos dos variedades para fusionar.",
        ),
      );
      return;
    }
    if (varietySelectionSpeciesIds.length > 1) {
      notify.warning(
        t(
          "crops.variety_merge_mixed_species",
          "Solo se pueden fusionar variedades de la misma especie.",
        ),
      );
      return;
    }
    const heaviest = [...varietyMergeRows].sort(
      (a, b) => b.assignment_count - a.assignment_count,
    )[0];
    setMergeContext({
      kind: "variety",
      ids: selectedVarietyIds,
      speciesId: varietySelectionSpeciesIds[0],
    });
    setMergeTargetId(heaviest?.id ?? selectedVarietyIds[0]);
  };

  const submitMerge = async () => {
    if (!mergeContext || !mergeTargetId) return;
    const sources = mergeContext.ids.filter((id) => id !== mergeTargetId);
    if (sources.length === 0) {
      notify.warning(
        t(
          "crops.merge_no_sources",
          "Selecciona al menos un elemento distinto del destino.",
        ),
      );
      return;
    }
    setMerging(true);
    try {
      const url =
        mergeContext.kind === "species"
          ? "/admin/crop-species/merge/"
          : "/admin/crop-varieties/merge/";
      const res = await apiClient.post<{
        moved_adjacent_crops?: number;
        moved_adjacent_crop_reviews?: number;
        reparented_varieties?: number;
        merged_varieties?: number;
      }>(url, { target_id: mergeTargetId, source_ids: sources });
      const movedRefs =
        (res.data?.moved_adjacent_crops ?? 0) +
        (res.data?.moved_adjacent_crop_reviews ?? 0);
      if (mergeContext.kind === "species") {
        notify.success(
          t(
            "crops.species_merge_success",
            "Fusionadas {{n}} especies · {{moved}} cultivos reasignados · {{rep}} variedades movidas · {{coll}} colisiones fusionadas",
            {
              n: sources.length,
              moved: movedRefs,
              rep: res.data?.reparented_varieties ?? 0,
              coll: res.data?.merged_varieties ?? 0,
            },
          ),
        );
      } else {
        notify.success(
          t(
            "crops.variety_merge_success",
            "Fusionadas {{n}} variedades · {{moved}} cultivos reasignados",
            { n: sources.length, moved: movedRefs },
          ),
        );
      }
      setMergeContext(null);
      setMergeTargetId(null);
      if (mergeContext.kind === "species") setSelectedSpeciesIds([]);
      else setSelectedVarietyIds([]);
      await load();
    } catch (e: any) {
      const msg =
        e?.response?.data?.detail ??
        t("crops.merge_error", "No se pudo fusionar");
      notify.error(msg);
    } finally {
      setMerging(false);
    }
  };

  // ----- table columns ----------------------------------------------

  const translationChips = (row: {
    name_en: string;
    name_it: string;
    name_pt: string;
  }) => {
    const entries: Array<{ code: string; label: string; value: string }> = [
      { code: "EN", label: "English", value: row.name_en },
      { code: "IT", label: "Italiano", value: row.name_it },
      { code: "PT", label: "Português", value: row.name_pt },
    ];
    return (
      <Space size={4} wrap>
        {entries.map((e) =>
          e.value ? (
            <Tooltip key={e.code} title={`${e.label}: ${e.value}`}>
              <Tag color="blue" style={{ margin: 0 }}>
                {e.code}
              </Tag>
            </Tooltip>
          ) : (
            <Tooltip
              key={e.code}
              title={`${e.label}: ${t("crops.no_translation", "sin traducir")}`}
            >
              <Tag color="default" style={{ margin: 0, opacity: 0.55 }}>
                {e.code}
              </Tag>
            </Tooltip>
          ),
        )}
      </Space>
    );
  };

  const speciesColumns = [
    {
      title: t("crops.species_name", "Especie"),
      dataIndex: "name",
      key: "name",
      ellipsis: { showTitle: false } as any,
      render: (v: string, row: Species) => (
        <Tooltip
          title={
            row.scientific_name ? `${v} — ${row.scientific_name}` : v
          }
          placement="topLeft"
          mouseEnterDelay={0.25}
        >
          <strong>{v}</strong>
          {row.scientific_name ? (
            <Text type="secondary" italic style={{ marginLeft: 8 }}>
              {row.scientific_name}
            </Text>
          ) : null}
        </Tooltip>
      ),
      sorter: (a: Species, b: Species) => a.name.localeCompare(b.name),
    },
    {
      title: t("crops.translations", "Traducciones"),
      key: "translations",
      width: 220,
      render: (_: any, row: Species) => translationChips(row),
    },
    {
      title: t("crops.variety_count", "Variedades"),
      dataIndex: "variety_count",
      key: "variety_count",
      width: 130,
      align: "center" as const,
      render: (n: number) =>
        n > 0 ? <Tag color="purple">{n}</Tag> : <Tag color="default">0</Tag>,
      sorter: (a: Species, b: Species) => a.variety_count - b.variety_count,
    },
    {
      title: t("crops.assignment_count", "Cultivos adyacentes"),
      dataIndex: "assignment_count",
      key: "assignment_count",
      width: 180,
      align: "center" as const,
      sorter: (a: Species, b: Species) =>
        a.assignment_count - b.assignment_count,
      render: (n: number) =>
        n > 0 ? <Tag color="blue">{n}</Tag> : <Tag color="default">0</Tag>,
    },
    {
      title: t("common.actions", "Acciones"),
      key: "actions",
      width: 200,
      align: "right" as const,
      render: (_: any, row: Species) => {
        const blocked = row.assignment_count > 0 || row.variety_count > 0;
        return (
          <Space size="small">
            <Tooltip
              title={t("crops.add_variety", "Añadir variedad a esta especie")}
            >
              <Button
                size="small"
                icon={<PlusOutlined />}
                onClick={() => openCreateVariety(row.id)}
              />
            </Tooltip>
            <Tooltip title={t("common.edit", "Editar")}>
              <Button
                size="small"
                icon={<EditOutlined />}
                onClick={() => openEditSpecies(row)}
              />
            </Tooltip>
            <Popconfirm
              title={t(
                "crops.species_delete_confirm",
                "¿Eliminar esta especie?",
              )}
              description={
                row.assignment_count > 0
                  ? t(
                      "crops.species_delete_blocked_assigned",
                      "Tiene cultivos asignados. Fusiónala primero.",
                    )
                  : row.variety_count > 0
                  ? t(
                      "crops.species_delete_blocked_varieties",
                      "Tiene variedades. Elimínalas o fusiónalas antes.",
                    )
                  : t(
                      "crops.species_delete_warning",
                      "Esta acción no se puede deshacer.",
                    )
              }
              onConfirm={() => deleteSpecies(row)}
              okText={t("common.yes", "Sí")}
              cancelText={t("common.no", "No")}
              okButtonProps={{ danger: true }}
              disabled={blocked}
            >
              <Tooltip
                title={
                  row.assignment_count > 0
                    ? t(
                        "crops.species_delete_blocked_tip_assigned",
                        "Tiene cultivos adyacentes asignados",
                      )
                    : row.variety_count > 0
                    ? t(
                        "crops.species_delete_blocked_tip_varieties",
                        "Tiene variedades hijas",
                      )
                    : t("common.delete", "Eliminar")
                }
              >
                <Button
                  size="small"
                  danger
                  icon={<DeleteOutlined />}
                  disabled={blocked}
                />
              </Tooltip>
            </Popconfirm>
          </Space>
        );
      },
    },
  ];

  const renderVarietiesSubtable = (parent: Species) => {
    const rows = varietiesBySpecies[parent.id] ?? [];
    const columns = [
      {
        title: t("crops.variety_name", "Variedad"),
        dataIndex: "name",
        key: "name",
        render: (v: string) => <strong>{v}</strong>,
      },
      {
        title: t("crops.translations", "Traducciones"),
        key: "translations",
        width: 220,
        render: (_: any, row: Variety) => translationChips(row),
      },
      {
        title: t("crops.assignment_count", "Cultivos adyacentes"),
        dataIndex: "assignment_count",
        key: "assignment_count",
        width: 180,
        align: "center" as const,
        render: (n: number) =>
          n > 0 ? <Tag color="blue">{n}</Tag> : <Tag color="default">0</Tag>,
      },
      {
        title: t("common.actions", "Acciones"),
        key: "actions",
        width: 130,
        align: "right" as const,
        render: (_: any, row: Variety) => (
          <Space size="small">
            <Tooltip title={t("common.edit", "Editar")}>
              <Button
                size="small"
                icon={<EditOutlined />}
                onClick={() => openEditVariety(row)}
              />
            </Tooltip>
            <Popconfirm
              title={t(
                "crops.variety_delete_confirm",
                "¿Eliminar esta variedad?",
              )}
              description={
                row.assignment_count > 0
                  ? t(
                      "crops.variety_delete_blocked",
                      "No puedes eliminar una variedad asignada. Fusiónala primero.",
                    )
                  : t(
                      "crops.variety_delete_warning",
                      "Esta acción no se puede deshacer.",
                    )
              }
              onConfirm={() => deleteVariety(row)}
              okText={t("common.yes", "Sí")}
              cancelText={t("common.no", "No")}
              okButtonProps={{ danger: true }}
              disabled={row.assignment_count > 0}
            >
              <Tooltip
                title={
                  row.assignment_count > 0
                    ? t(
                        "crops.variety_delete_blocked_tooltip",
                        "Tiene cultivos asignados — no se puede eliminar",
                      )
                    : t("common.delete", "Eliminar")
                }
              >
                <Button
                  size="small"
                  danger
                  icon={<DeleteOutlined />}
                  disabled={row.assignment_count > 0}
                />
              </Tooltip>
            </Popconfirm>
          </Space>
        ),
      },
    ];

    return (
      <div style={{ padding: "8px 16px 16px 48px" }}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 8,
          }}
        >
          <Text type="secondary">
            {t("crops.varieties_for", "Variedades de")}{" "}
            <strong>{parent.name}</strong>
          </Text>
          <Button
            size="small"
            type="dashed"
            icon={<PlusOutlined />}
            onClick={() => openCreateVariety(parent.id)}
          >
            {t("crops.create_variety", "Nueva variedad")}
          </Button>
        </div>
        <Table<Variety>
          dataSource={rows}
          columns={columns}
          rowKey="id"
          size="small"
          pagination={false}
          rowSelection={{
            type: "checkbox",
            selectedRowKeys: selectedVarietyIds.filter((id) =>
              rows.some((r) => r.id === id),
            ),
            onChange: (keys) => {
              const rowKeys = keys as string[];
              // Replace this species' variety selection with the new keys,
              // keep the rest of the cross-species selection intact.
              const others = selectedVarietyIds.filter(
                (id) => !rows.some((r) => r.id === id),
              );
              setSelectedVarietyIds([...others, ...rowKeys]);
            },
            columnWidth: 48,
          }}
          locale={{
            emptyText: t(
              "crops.no_varieties",
              "Esta especie no tiene variedades todavía.",
            ),
          }}
        />
      </div>
    );
  };

  return (
    <Card>
      <PageHeader
        icon={<TreeIcon />}
        accent="#52C41A"
        title={t("crops.title", "Especies y variedades de cultivo")}
        subtitle={t(
          "crops.subtitle",
          "Catálogo usado en los cultivos adyacentes de las auditorías. Las variedades cuelgan de su especie y se pueden fusionar entre sí dentro de la misma especie.",
        )}
        extra={
          <Space wrap>
            <Link to="/admin/crops/legacy">
              <Button icon={<HistoryOutlined />}>
                {t(
                  "crops.legacy_migration_link",
                  "Migrar variedades antiguas",
                )}
              </Button>
            </Link>
            <Button icon={<ReloadOutlined />} onClick={() => void load()}>
              {t("common.reload", "Refrescar")}
            </Button>
            <Tooltip
              title={
                selectedVarietyIds.length < 2
                  ? t(
                      "crops.variety_merge_hint_tooltip",
                      "Marca las casillas de 2 o más variedades (de la misma especie) para fusionarlas",
                    )
                  : varietySelectionSpeciesIds.length > 1
                  ? t(
                      "crops.variety_merge_mixed_tooltip",
                      "La selección mezcla variedades de distintas especies — fusiona solo dentro de una misma especie",
                    )
                  : ""
              }
            >
              <Button
                icon={<MergeCellsOutlined />}
                onClick={openVarietyMerge}
                disabled={
                  selectedVarietyIds.length < 2 ||
                  varietySelectionSpeciesIds.length > 1
                }
                type={
                  selectedVarietyIds.length >= 2 &&
                  varietySelectionSpeciesIds.length <= 1
                    ? "primary"
                    : "default"
                }
              >
                {t("crops.variety_merge", "Fusionar variedades")}
                {selectedVarietyIds.length > 0 &&
                  ` (${selectedVarietyIds.length})`}
              </Button>
            </Tooltip>
            <Tooltip
              title={
                selectedSpeciesIds.length < 2
                  ? t(
                      "crops.species_merge_hint_tooltip",
                      "Marca las casillas de 2 o más especies en la tabla para fusionarlas",
                    )
                  : ""
              }
            >
              <Button
                icon={<MergeCellsOutlined />}
                onClick={openSpeciesMerge}
                type={selectedSpeciesIds.length >= 2 ? "primary" : "default"}
              >
                {t("crops.species_merge", "Fusionar especies")}
                {selectedSpeciesIds.length > 0 &&
                  ` (${selectedSpeciesIds.length})`}
              </Button>
            </Tooltip>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={openCreateSpecies}
            >
              {t("crops.create_species", "Nueva especie")}
            </Button>
          </Space>
        }
      />

      {selectedSpeciesIds.length < 2 && selectedVarietyIds.length < 2 && (
        <Alert
          showIcon
          type="info"
          style={{ marginBottom: 12 }}
          message={t(
            "crops.merge_banner",
            "Cómo fusionar: despliega una especie para ver sus variedades. Marca 2+ especies o 2+ variedades (de la misma especie) y pulsa el botón Fusionar correspondiente.",
          )}
        />
      )}

      {varietySelectionSpeciesIds.length > 1 && (
        <Alert
          showIcon
          type="warning"
          style={{ marginBottom: 12 }}
          message={t(
            "crops.variety_merge_mixed_warning",
            "Has seleccionado variedades de distintas especies. Solo se pueden fusionar variedades dentro de la misma especie — desmarca las de otras especies para continuar.",
          )}
        />
      )}

      <Table<Species>
        dataSource={species}
        columns={speciesColumns}
        rowKey="id"
        loading={loading}
        pagination={{ pageSize: 25, showSizeChanger: true }}
        scroll={{ x: 1100 }}
        rowSelection={{
          type: "checkbox",
          selectedRowKeys: selectedSpeciesIds,
          onChange: (keys) => setSelectedSpeciesIds(keys as string[]),
          columnWidth: 48,
          fixed: "left",
        }}
        expandable={{
          expandedRowRender: renderVarietiesSubtable,
          rowExpandable: () => true,
        }}
      />

      {/* Create / Edit modal — switches form by editorKind */}
      <Modal
        title={
          editorKind === "species"
            ? editingSpecies
              ? t("crops.species_edit_title", "Editar especie")
              : t("crops.species_create_title", "Nueva especie")
            : editingVariety
              ? t("crops.variety_edit_title", "Editar variedad")
              : t("crops.variety_create_title", "Nueva variedad")
        }
        open={editorOpen}
        onCancel={() => (saving ? undefined : setEditorOpen(false))}
        onOk={submitEditor}
        confirmLoading={saving}
        okText={t("common.save", "Guardar")}
        cancelText={t("common.cancel", "Cancelar")}
        destroyOnHidden
        width={560}
      >
        {editorKind === "species" ? (
          <Form form={speciesForm} layout="vertical">
            <Form.Item
              label={t("crops.species_name_es", "Nombre (Español)")}
              name="name"
              rules={[
                {
                  required: true,
                  message: t(
                    "crops.name_required",
                    "El nombre es obligatorio",
                  ),
                },
                { max: 100 },
              ]}
            >
              <Input
                maxLength={100}
                placeholder={t(
                  "crops.species_name_placeholder",
                  "Ej: Tomate",
                )}
                autoFocus
              />
            </Form.Item>
            <Form.Item
              label={t("crops.scientific_name", "Nombre científico")}
              name="scientific_name"
              rules={[{ max: 200 }]}
            >
              <Input
                maxLength={200}
                placeholder={t(
                  "crops.scientific_placeholder",
                  "Ej: Solanum lycopersicum",
                )}
              />
            </Form.Item>
            <Text
              type="secondary"
              style={{
                fontSize: 12,
                display: "block",
                marginTop: 4,
                marginBottom: 8,
              }}
            >
              {t(
                "crops.translations_hint",
                "Traducciones opcionales — se usan en los exports de Excel y en la UI según el idioma del usuario.",
              )}
            </Text>
            <Form.Item label="Nombre (English)" name="name_en" rules={[{ max: 100 }]}>
              <Input maxLength={100} placeholder="e.g. Tomato" />
            </Form.Item>
            <Form.Item label="Nome (Italiano)" name="name_it" rules={[{ max: 100 }]}>
              <Input maxLength={100} placeholder="es. Pomodoro" />
            </Form.Item>
            <Form.Item label="Nome (Português)" name="name_pt" rules={[{ max: 100 }]}>
              <Input maxLength={100} placeholder="ex: Tomate" />
            </Form.Item>
          </Form>
        ) : (
          <Form form={varietyForm} layout="vertical">
            <Form.Item name="species" hidden>
              <Input type="hidden" />
            </Form.Item>
            {varietyParentSpeciesId && (
              <Alert
                showIcon
                type="info"
                style={{ marginBottom: 12 }}
                message={t(
                  "crops.variety_parent_hint",
                  "Especie padre: {{name}}",
                  {
                    name:
                      species.find((s) => s.id === varietyParentSpeciesId)
                        ?.name ?? "",
                  },
                )}
              />
            )}
            <Form.Item
              label={t("crops.variety_name_es", "Nombre (Español)")}
              name="name"
              rules={[
                {
                  required: true,
                  message: t(
                    "crops.name_required",
                    "El nombre es obligatorio",
                  ),
                },
                { max: 100 },
              ]}
            >
              <Input
                maxLength={100}
                placeholder={t(
                  "crops.variety_name_placeholder",
                  "Ej: Cherry",
                )}
                autoFocus
              />
            </Form.Item>
            <Text
              type="secondary"
              style={{
                fontSize: 12,
                display: "block",
                marginTop: 4,
                marginBottom: 8,
              }}
            >
              {t(
                "crops.translations_hint",
                "Traducciones opcionales — se usan en los exports de Excel y en la UI según el idioma del usuario.",
              )}
            </Text>
            <Form.Item label="Nombre (English)" name="name_en" rules={[{ max: 100 }]}>
              <Input maxLength={100} placeholder="e.g. Cherry" />
            </Form.Item>
            <Form.Item label="Nome (Italiano)" name="name_it" rules={[{ max: 100 }]}>
              <Input maxLength={100} placeholder="es. Ciliegino" />
            </Form.Item>
            <Form.Item label="Nome (Português)" name="name_pt" rules={[{ max: 100 }]}>
              <Input maxLength={100} placeholder="ex: Cereja" />
            </Form.Item>
          </Form>
        )}
      </Modal>

      {/* Merge modal */}
      <Modal
        title={
          mergeContext?.kind === "species"
            ? t("crops.species_merge_title", "Fusionar especies")
            : t("crops.variety_merge_title", "Fusionar variedades")
        }
        open={!!mergeContext}
        onCancel={() => {
          if (!merging) {
            setMergeContext(null);
            setMergeTargetId(null);
          }
        }}
        onOk={submitMerge}
        confirmLoading={merging}
        okText={t("crops.merge_apply", "Fusionar")}
        cancelText={t("common.cancel", "Cancelar")}
        destroyOnHidden
        okButtonProps={{ danger: true }}
        width={560}
      >
        <Text type="secondary">
          {mergeContext?.kind === "species"
            ? t(
                "crops.species_merge_hint",
                "Selecciona la especie que quieres mantener. Las demás se eliminarán, sus cultivos adyacentes se reasignarán y sus variedades hijas se re-parentarán al destino (fusionando colisiones por nombre).",
              )
            : t(
                "crops.variety_merge_hint",
                "Selecciona la variedad que quieres mantener. Las demás se eliminarán y todos sus cultivos adyacentes se reasignarán a la variedad elegida.",
              )}
        </Text>
        <div style={{ marginTop: 12 }}>
          <Radio.Group
            value={mergeTargetId}
            onChange={(e) => setMergeTargetId(e.target.value)}
          >
            <Space direction="vertical">
              {(mergeContext?.kind === "species"
                ? speciesMergeRows
                : varietyMergeRows
              ).map((row) => (
                <Radio key={row.id} value={row.id}>
                  <strong>{row.name}</strong>{" "}
                  <Text type="secondary">
                    ({row.assignment_count}{" "}
                    {row.assignment_count === 1
                      ? t("crops.crop_one", "cultivo")
                      : t("crops.crop_many", "cultivos")})
                  </Text>
                </Radio>
              ))}
            </Space>
          </Radio.Group>
        </div>
      </Modal>
    </Card>
  );
};

export default CropsList;
